import { calculateDistance, calculateBearing } from './coordinates';
import { PlannerHomeBase, PlannerWaypoint } from '../types/missionPlanner';

/** A plain lat/lng point (home base target for the RTH leg). */
export interface LatLngPoint {
  lat: number;
  lng: number;
}

/**
 * The home base the Return-to-Home leg targets: the custom home base if set,
 * otherwise the mission start point (first waypoint). Null when neither exists.
 */
export const effectiveHomeBase = (
  waypoints: PlannerWaypoint[],
  homeBase: PlannerHomeBase | null
): LatLngPoint | null => {
  if (homeBase) {
    return { lat: homeBase.lat, lng: homeBase.lng };
  }
  if (waypoints.length > 0) {
    return { lat: waypoints[0].lat, lng: waypoints[0].lng };
  }
  return null;
};

/**
 * An endpoint of a route leg. Every waypoint is one structurally; the home
 * base the loop closes onto is one too, even when it is not a waypoint.
 */
export interface RoutePoint {
  id: string;
  /** 1-based waypoint sequence; 0 for the synthetic home-base endpoint. */
  order: number;
  lat: number;
  lng: number;
}

/** Id of the synthetic endpoint standing in for a *custom* home base. */
export const HOME_POINT_ID = 'home-base';

/** One leg of the draft route, between two consecutive route points. */
export interface RouteSegment {
  from: RoutePoint;
  to: RoutePoint;
  /** Great-circle length of this leg, in kilometres. */
  distanceKm: number;
  /** Initial bearing from `from` to `to`, degrees clockwise from north. */
  bearingDeg: number;
  /** Distance from the first waypoint up to and including this leg (km). */
  cumulativeKm: number;
  /** True for the leg that closes the loop back to home (#404). */
  closing: boolean;
}

/**
 * The endpoint the closing leg targets, or null when no closing leg exists:
 * RTH is off, there is nothing to return from, or the last waypoint already
 * sits on home (a zero-length leg).
 *
 * With no custom home base the target is waypoint 1 itself — the same object,
 * so the closing leg shares its identity and every consumer keyed on point ids
 * (clustering, React keys) resolves it to a real waypoint.
 */
export const closingPointFor = (
  waypoints: PlannerWaypoint[],
  homeBase: PlannerHomeBase | null,
  returnToHome: boolean
): RoutePoint | null => {
  if (!returnToHome || waypoints.length === 0) {
    return null;
  }
  const target: RoutePoint = homeBase
    ? { id: HOME_POINT_ID, order: 0, lat: homeBase.lat, lng: homeBase.lng }
    : waypoints[0];
  const last = waypoints[waypoints.length - 1];
  if (last.lat === target.lat && last.lng === target.lng) {
    return null;
  }
  return target;
};

/** Aggregate metrics for the whole draft route. */
export interface RouteMetrics {
  segments: RouteSegment[];
  /** Sum of every leg, the loop-closing leg included (km). */
  totalKm: number;
  /** Direct first→last *waypoint* distance (km) — never via home. */
  straightLineKm: number;
  /**
   * Route efficiency in [0, 1]: straight-line / total. 1.0 means the route is
   * already a straight line; lower means more detour. 0 when < 2 waypoints.
   */
  efficiency: number;
}

/**
 * Compute per-segment and aggregate distance metrics for an ordered list of
 * waypoints. Pure — no Leaflet, no Redux — so it is trivially testable and can
 * be memoised by callers.
 *
 * `closingTo` (from `closingPointFor`) appends the loop-closing leg as an
 * ordinary segment, so it counts towards `totalKm` and the cumulative running
 * total exactly like any other leg — the drone flies it either way (#404).
 * Segment `i` always starts at `waypoints[i]`, closing leg included, so the
 * index doubles as the insert position for splitting that leg.
 */
export const computeRouteMetrics = (
  waypoints: PlannerWaypoint[],
  closingTo: RoutePoint | null = null
): RouteMetrics => {
  const segments: RouteSegment[] = [];
  let cumulative = 0;

  const push = (from: RoutePoint, to: RoutePoint, closing: boolean): void => {
    const distanceKm = calculateDistance(from.lat, from.lng, to.lat, to.lng);
    cumulative += distanceKm;
    segments.push({
      from,
      to,
      distanceKm,
      bearingDeg: calculateBearing(from.lat, from.lng, to.lat, to.lng),
      cumulativeKm: cumulative,
      closing,
    });
  };

  for (let i = 0; i < waypoints.length - 1; i += 1) {
    push(waypoints[i], waypoints[i + 1], false);
  }
  if (closingTo) {
    push(waypoints[waypoints.length - 1], closingTo, true);
  }

  const totalKm = cumulative;
  const straightLineKm =
    waypoints.length >= 2
      ? calculateDistance(
          waypoints[0].lat,
          waypoints[0].lng,
          waypoints[waypoints.length - 1].lat,
          waypoints[waypoints.length - 1].lng
        )
      : 0;
  const efficiency = totalKm > 0 ? straightLineKm / totalKm : 0;

  return { segments, totalKm, straightLineKm, efficiency };
};
